import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Popover,
  styled,
  InputBase,
  IconButton,
  FormHelperText,
  Button,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { PickersDay } from "@mui/x-date-pickers";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import { useTheme } from "@mui/material/styles";

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "selectedDay" && prop !== "inRange",
})<{ selectedDay?: boolean; inRange?: boolean }>(
  ({ selectedDay, inRange }) => ({
    ...(selectedDay && {
      backgroundColor: "#6455ED",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#5C50D5",
      },
    }),
    ...(inRange && {
      backgroundColor: "#E6E4FF",
      color: "#000",
    }),
  }),
);

// Define the type for the range object
interface DateRange {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

// Define the type for the range object
interface DateRange {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

interface DateRangePickerNewProps {
  onRangeSelect: (range: DateRange) => void;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  placeholder?: string;
  disableFutureDates?: boolean;
  zIndex?: number;
}

export const DateRangePickerNew: React.FC<DateRangePickerNewProps> = ({
  onRangeSelect,
  startDate,
  endDate,
  placeholder,
  disableFutureDates = true,
  zIndex = 1000,
}: DateRangePickerNewProps) => {
  const [range, setRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const hasValue = Boolean(range.startDate || range.endDate);

  const handleDateChange = (date: Dayjs | null) => {
    if (!date) return;

    if (!range.startDate || (range.startDate && range.endDate)) {
      setRange({ startDate: date, endDate: null });
      setError(null);
    } else {
      if (
        date &&
        (range.startDate.isBefore(date) || range.startDate.isSame(date, "day"))
      ) {
        const selected_range = { ...range, endDate: date };
        setRange(selected_range);
        onRangeSelect(selected_range);
        setOpen(false);
        setError(null);
      } else {
        setError("End date cannot be before start date.");
        setRange({ startDate: date, endDate: null });
      }
    }
  };

  const handleTodayClick = () => {
    const today = dayjs();

    // Check if today is after start date or same as start date
    if (
      range.startDate &&
      (range.startDate.isBefore(today) || range.startDate.isSame(today, "day"))
    ) {
      const selected_range = { ...range, endDate: today };
      setRange(selected_range);
      onRangeSelect(selected_range);
      setOpen(false);
      setError(null);
    } else {
      setError("Today cannot be before start date.");
    }
  };

  const handleMonthEndClick = () => {
    if (!range.startDate) return;

    // Get the last date of the month of the start date
    const monthEnd = range.startDate.endOf("month");

    // Since we're using the start date's month, it will always be valid
    const selected_range = { ...range, endDate: monthEnd };
    setRange(selected_range);
    onRangeSelect(selected_range);
    setOpen(false);
    setError(null);
  };

  useEffect(() => {
    setRange({ startDate, endDate });
  }, [startDate, endDate]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box ref={ref} sx={{ position: "relative", width: "100%" }}>
        {hasValue && placeholder ? (
          <Box
            sx={{
              position: "absolute",
              top: "-8px",
              left: "10px",
              px: 0.5,
              fontSize: "0.7rem",
              lineHeight: 1,
              color: "text.secondary",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : theme.palette.customColors.white,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {placeholder}
          </Box>
        ) : null}
        <InputBase
          readOnly
          placeholder={!hasValue ? placeholder : undefined}
          value={
            range.startDate && range.endDate
              ? `${range.startDate.format(
                "YYYY-MM-DD",
              )} - ${range.endDate.format("YYYY-MM-DD")}`
              : range.startDate
                ? `${range.startDate.format("YYYY-MM-DD")}`
                : ""
          }
          sx={{
            width: "100%",
            fontSize: "0.8rem",
            padding: "3px 10px",
            border: "1px solid #ddd",
            borderRadius: "8px",
          }}
          endAdornment={
            <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
              <CalendarMonth fontSize="small" />
            </IconButton>
          }
        />

        {error && (
          <FormHelperText error sx={{ fontSize: "0.75rem", marginTop: "5px" }}>
            {error}
          </FormHelperText>
        )}

        <Popover
          open={open}
          anchorEl={ref.current}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                boxShadow: "rgba(0, 0, 0, 0.08) 0px 4px 12px",
                borderRadius: "16px",
                overflow: "hidden",
                zIndex,
              },
            },
          }}
          disableScrollLock
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : theme.palette.customColors.white,
            }}
          >
            {/* Quick selection buttons - always visible but toggle enabled/disabled */}
            <Box
              sx={{
                padding: "4px 8px",
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleMonthEndClick}
                disabled={!range.startDate || !!range.endDate}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  padding: "0px 8px",
                  minWidth: "auto",
                  borderRadius: "24px",
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.customColors.white,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.customColors.white,
                  },
                  "&:disabled": {
                    borderColor: "#ccc",
                    color: "#ccc",
                  },
                }}
              >
                Month End
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleTodayClick}
                disabled={!range.startDate || !!range.endDate}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  padding: "0px 8px",
                  minWidth: "auto",
                  borderColor: theme.palette.primary.main,
                  borderRadius: "24px",
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.customColors.white,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.customColors.white,
                  },
                  "&:disabled": {
                    borderColor: "#ccc",
                    color: "#ccc",
                  },
                }}
              >
                Today
              </Button>
            </Box>

            <DateCalendar
              value={range.startDate || range.endDate}
              onChange={handleDateChange}
              shouldDisableDate={
                disableFutureDates
                  ? (date) => date.isAfter(dayjs(), "day")
                  : undefined
              }
              sx={{ width: "300px", height: "310px" }}
              slots={{
                day: (props) => {
                  const selectedDay =
                    (range.startDate &&
                      props.day.isSame(range.startDate, "day")) ||
                    (range.endDate && props.day.isSame(range.endDate, "day"));
                  const inRange =
                    range.startDate &&
                    range.endDate &&
                    props.day.isAfter(range.startDate, "day") &&
                    props.day.isBefore(range.endDate, "day");

                  return (
                    <CustomPickersDay
                      {...props}
                      selectedDay={!!selectedDay}
                      inRange={!!inRange}
                    />
                  );
                },
              }}
            />
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};
